import React from 'react';
import { Box, Button, Sheet, Tooltip, Typography } from '@mui/joy';

interface ProviderSteeringNoteCardProps {
  note: string;
  updatedAt: string | null;
  updatedBy?: string | null;
  iAmOwner: boolean;
  onEdit: () => void;
}

/**
 * The API stores this timestamp as naive UTC, so it serialises without an offset
 * — and JS reads an offset-less date-time as *local*, which would show the note
 * as edited hours from when it was. Pin it to UTC before formatting.
 *
 * That UTC reading is only safe because the column is stamped in Python
 * (`TeamProviderNote` in `shared/models/models.py`) rather than by the database
 * clock: a server-side `now()` is UTC on SQLite but the session's local time on
 * Postgres, and this would then be wrong by the server's offset.
 */
function formatUpdatedAt(updatedAt: string): string {
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(updatedAt);
  const parsed = new Date(hasZone ? updatedAt : `${updatedAt}Z`);
  return Number.isNaN(parsed.getTime()) ? updatedAt : parsed.toLocaleString();
}

/**
 * The team's provider steering note, shown above the provider list.
 *
 * It sits here rather than on any provider row because it is one note about the
 * choice between them. The empty state is deliberately plain: having no note is
 * the ordinary case, not something to nag about.
 */
export default function ProviderSteeringNoteCard({
  note,
  updatedAt,
  updatedBy,
  iAmOwner,
  onEdit,
}: ProviderSteeringNoteCardProps) {
  return (
    <Sheet
      variant="soft"
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 'sm',
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography level="title-sm">Provider steering note</Typography>
        {note ? (
          <Typography
            level="body-sm"
            sx={{ mt: 0.5, whiteSpace: 'pre-wrap', color: 'text.secondary' }}
          >
            {note}
          </Typography>
        ) : (
          <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.tertiary' }}>
            No note set. Agents choosing hardware will pick on the usual merits.
          </Typography>
        )}
        {note && updatedAt && (
          <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
            Last updated {formatUpdatedAt(updatedAt)}
            {updatedBy ? ` by ${updatedBy}` : ''}
          </Typography>
        )}
      </Box>
      <Tooltip
        title={
          iAmOwner
            ? 'Guidance for whoever — or whatever — picks a provider'
            : 'Only owners can edit the provider steering note'
        }
      >
        <span>
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            onClick={onEdit}
            disabled={!iAmOwner}
            sx={{ flexShrink: 0 }}
          >
            {note ? 'Edit' : 'Add'}
          </Button>
        </span>
      </Tooltip>
    </Sheet>
  );
}
